/**
 * @fileoverview How this gardener likes their shared pictures to look.
 *
 * Kept on the device rather than the account, like the other viewing
 * preferences: it describes the way the app behaves in the hand holding it,
 * and asks nothing of the server.
 *
 * Every choice is kept as it is made and is there next time: the size, the
 * layout, the background, what is printed and how large, and which parts of
 * the date (the day, the year; until changed here, as the gardener's Profile >
 * Public pages has them). There used to be a
 * switch for remembering; keeping them is what anyone would expect, so it
 * went. The words are never kept, because a caption written for one
 * photograph is wrong on the next one.
 *
 * Someone sharing for the first time starts on the size, layout and card
 * ground in the Grow settings (sharing.DEFAULT_SIZE, DEFAULT_LAYOUT,
 * DEFAULT_GROUND): 4:5, collage, garden green, unless the owner changes them;
 * and on what is printed as the server's settings say (sharing.DEFAULT_ELEMENTS
 * for a plant, DEFAULT_COVER_ELEMENTS for a bed's own pictures,
 * DEFAULT_PLANT_PHOTO_ELEMENTS for the photos of a bed's plants): the
 * gardener's own words alone, unless the owner changes them. Until the app has
 * heard the server's answer, nothing is printed.
 *
 * Where: the share sheet (SharePlantDialog) and the season review's size.
 * API: none; localStorage under SHARE_STYLE_STORAGE_KEY.
 * Settings: Grow settings sharing.DEFAULT_SIZE, sharing.DEFAULT_LAYOUT,
 * sharing.DEFAULT_GROUND, sharing.DEFAULT_ELEMENTS, sharing.DEFAULT_COVER_ELEMENTS,
 * sharing.DEFAULT_PLANT_PHOTO_ELEMENTS.
 *
 * @module shareStyle
 */

import type { CardGround, ShareElements, ShareLayout, ShareSize } from './shareFrames';
import { LINK_KEYS, type LinkKey } from '@/shared/lib/socialLinks';
import { growSetting } from '@/shared/lib/growSettings';
import { CARD_GROUND_VALUES, SHARE_LAYOUT_VALUES, SHARE_SIZE_VALUES, TEXT_SCALES } from './shareFrames';
import { SHARE_STYLE_STORAGE_KEY } from '@/config/tunables/plantList';

/** Where the choices are kept on the device. */
const KEY = SHARE_STYLE_STORAGE_KEY;

/** The switches a plant share keeps (ShareElements without a location's two extras); stage is no longer printed. */
const PLANT_SWITCHES = ['name', 'variety', 'stage', 'date', 'words', 'handle'] as const;

/** Which of the three starting sets of switches: a plant share's, a bed's own pictures', the photos of a bed's plants'. */
export type StartingSet = 'plant' | 'cover' | 'plantPhoto';

/**
 * What a share prints to start, as the server's Grow settings say: every switch off except those the setting lists.
 * Until the app has heard the setting, every switch is off.
 *
 * @param set - Whose switches: a plant share's, a bed's own pictures', or the photos of a bed's plants'.
 * @returns The switches. A bed's own pictures also carry count, and the photos of its plants place.
 */
export function startingElements(set: StartingSet): ShareElements {
  const on = growSetting(
    set === 'plant' ? 'sharing.DEFAULT_ELEMENTS'
      : set === 'cover' ? 'sharing.DEFAULT_COVER_ELEMENTS' : 'sharing.DEFAULT_PLANT_PHOTO_ELEMENTS',
  ) ?? [];
  const elements = Object.fromEntries(PLANT_SWITCHES.map((key) => [key, on.includes(key)])) as unknown as ShareElements;
  if (set === 'cover') elements.count = on.includes('count');
  if (set === 'plantPhoto') elements.place = on.includes('place');
  return elements;
}

/** The sharer's choices, kept between shares. */
export interface ShareStyle {
  /** The frame's size: square, story or pin. */
  size: ShareSize;
  /** How the photographs are arranged. */
  layout: ShareLayout;
  /** What is printed on the picture. */
  elements: ShareElements;
  /**
   * Which of their links to print as the handle, or null for the first one
   * they have. Somebody with an Instagram and a TikTok posts to one of them
   * at a time, and the frame should carry the right one.
   */
  handle: LinkKey | null;
  /** The card layout's background. */
  cardGround: CardGround;
  /** How large the lettering is: one of TEXT_SCALES, 1 as designed. */
  textScale: number;
  /** Whether the printed date says its day of the month; unset follows Profile > Public pages. */
  dateDay?: boolean;
  /** Whether the printed date says its year; unset follows Profile > Public pages. */
  dateYear?: boolean;
}

/**
 * What a first share starts on: the Grow settings' size, layout and card
 * ground, each only when it is one the sharer offers, what is printed as the
 * Grow settings say (startingElements), and everything at the size it was
 * designed.
 *
 * @returns The defaults.
 */
export function defaultShareStyle(): ShareStyle {
  const size = growSetting('sharing.DEFAULT_SIZE') ?? '';
  const layout = growSetting('sharing.DEFAULT_LAYOUT') ?? '';
  const ground = growSetting('sharing.DEFAULT_GROUND') ?? '';
  return {
    size: (SHARE_SIZE_VALUES as readonly string[]).includes(size) ? (size as ShareSize) : 'portrait',
    layout: (SHARE_LAYOUT_VALUES as readonly string[]).includes(layout) ? (layout as ShareLayout) : 'collage',
    elements: startingElements('plant'),
    handle: null,
    cardGround: (CARD_GROUND_VALUES as readonly string[]).includes(ground) ? (ground as CardGround) : 'green',
    textScale: 1,
  };
}

/**
 * Narrows whatever was in storage to something the dialog can use; anything
 * missing or no longer offered takes its default.
 *
 * @param value - As parsed from storage.
 * @returns A usable style.
 */
function clean(value: unknown): ShareStyle {
  const fallback = defaultShareStyle();
  if (!value || typeof value !== 'object') return fallback;
  const raw = value as Partial<ShareStyle> & { elements?: Partial<ShareElements> };
  const size = SHARE_SIZE_VALUES.includes(raw.size as ShareSize)
    ? (raw.size as ShareSize) : fallback.size;
  const layout = SHARE_LAYOUT_VALUES.includes(raw.layout as ShareLayout)
    ? (raw.layout as ShareLayout) : fallback.layout;

  const elements = startingElements('plant');
  for (const key of PLANT_SWITCHES) {
    if (typeof raw.elements?.[key] === 'boolean') elements[key] = raw.elements[key] as boolean;
  }
  const handle = (LINK_KEYS as readonly string[]).includes(String(raw.handle))
    ? (raw.handle as LinkKey) : null;
  const cardGround = CARD_GROUND_VALUES.includes(raw.cardGround as CardGround)
    ? (raw.cardGround as CardGround) : fallback.cardGround;
  const textScale = (TEXT_SCALES as readonly number[]).includes(Number(raw.textScale))
    ? Number(raw.textScale) : fallback.textScale;
  return {
    size, layout, elements, handle, cardGround, textScale,
    ...(typeof raw.dateDay === 'boolean' ? { dateDay: raw.dateDay } : {}),
    ...(typeof raw.dateYear === 'boolean' ? { dateYear: raw.dateYear } : {}),
  };
}

/**
 * What the dialog should open on: the last choices, or the defaults.
 *
 * @returns The choices, always usable: anything stored that is no longer offered takes its default.
 */
export function readShareStyle(): ShareStyle {
  try {
    const stored = window.localStorage.getItem(KEY);
    if (!stored) return defaultShareStyle();
    return clean(JSON.parse(stored));
  } catch {
    // Unreadable, unparseable or unavailable all mean the same thing here.
    return defaultShareStyle();
  }
}

/**
 * Keeps these choices for next time.
 *
 * @param style - Everything chosen so far.
 */
export function writeShareStyle(style: ShareStyle): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(style));
  } catch {
    /* private mode; it just will not stick */
  }
}
